import React, { useMemo } from 'react';
import { Box, Grid, Chip, Typography } from '@mui/material';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import {
  ChargeCategory,
  CustomerProfile,
  Location,
  OrderChargeDraft,
  OrderSummary,
} from '../../../../types/sales.types';
import { formatCurrency } from '../../../../utils/formatters/money';
import { capitalizeSentence, formatPhoneNumber } from '../../../../utils/formatters/text';
import { getSaleInvoiceLabel } from '../../../../utils/sales/sale-invoice.utils';
import { useOrderInformationCard } from './OrderInformationCard.hooks';
import {
  StyledCard,
  StyledCardContent,
  CardTitle,
  InfoBox,
  InfoLabel,
  InfoValue,
} from './OrderInformationCard.styles';

interface OrderInformationCardProps {
  orderNumber: string;
  selectedLocation: Location | null;
  orderSummary: OrderSummary;
  selectedCustomer?: CustomerProfile | null;
  hasInvoice?: boolean;
  orderCharges?: OrderChargeDraft[];
  chargeCategories?: ChargeCategory[];
}

const OrderInformationCard: React.FC<OrderInformationCardProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  orderNumber,
  selectedLocation,
  orderSummary,
  selectedCustomer,
  hasInvoice = false,
  orderCharges = [],
  chargeCategories = [],
}) => {
  const { statusColor, statusText } = useOrderInformationCard({ orderSummary });

  const categoryLabelByCode = useMemo(
    () => new Map(chargeCategories.map((category) => [category.code, category.label])),
    [chargeCategories],
  );

  const hasCharges = orderCharges.length > 0;

  return (
    <StyledCard>
      <StyledCardContent>
        <CardTitle variant="h6">
          <ReceiptIcon color="primary" />
          Información de la venta
        </CardTitle>

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <InfoBox>
              <InfoLabel variant="body2">Sucursal</InfoLabel>
              <InfoValue variant="body1">{selectedLocation?.name}</InfoValue>
            </InfoBox>
          </Grid>

          <Grid item xs={6}>
            <InfoBox>
              <InfoLabel variant="body2">Estado</InfoLabel>
              <Chip label={statusText} color={statusColor as any} size="small" />
            </InfoBox>
          </Grid>

          <Grid item xs={6}>
            <InfoBox>
              <InfoLabel variant="body2">Factura</InfoLabel>
              <Chip
                label={getSaleInvoiceLabel(hasInvoice)}
                color={hasInvoice ? 'info' : 'default'}
                size="small"
              />
            </InfoBox>
          </Grid>

          <Grid item xs={12}>
            <InfoBox>
              <InfoLabel variant="body2">Cliente asociado</InfoLabel>
              <Chip
                label={selectedCustomer ? 'Asociado' : 'Sin cliente'}
                color={selectedCustomer ? 'success' : 'default'}
                size="small"
                sx={{ mb: 0.5 }}
              />
              <InfoValue variant="body1">
                {selectedCustomer
                  ? `${selectedCustomer.customerName} - CI: ${
                      selectedCustomer.identityDocumentNumber?.trim() || 'N/A'
                    } - ${selectedCustomer.customerEmail?.trim() || 'sin correo'} - ${
                      selectedCustomer.customerPhone?.trim()
                        ? formatPhoneNumber(selectedCustomer.customerPhone)
                        : 'sin celular'
                    }`
                  : 'Venta sin cliente'}
              </InfoValue>
            </InfoBox>
          </Grid>

          {hasCharges && (
            <Grid item xs={12}>
              <InfoBox>
                <InfoLabel variant="body2">Cargos adicionales</InfoLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.25 }}>
                  {orderCharges.map((charge) => (
                    <Box
                      key={charge.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {categoryLabelByCode.get(charge.categoryCode) ?? charge.categoryCode}
                        </Typography>
                        {charge.description?.trim() ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.15 }}
                          >
                            {capitalizeSentence(charge.description)}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                        {formatCurrency(charge.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </InfoBox>
            </Grid>
          )}
        </Grid>
      </StyledCardContent>
    </StyledCard>
  );
};

export default OrderInformationCard;
